import {
  AttendanceDto,
  ClassProgrammeDto,
  ClassroomGroupDto,
  HolidayDto,
  LearnerDto,
} from '@ecdlink/core';
import { AttendanceStatus, Colours, SubTitleShape } from '@ecdlink/ui';
import {
  addDays,
  addHours,
  differenceInDays,
  endOfDay,
  endOfMonth,
  format,
  getDate,
  getDay,
  getDayOfYear,
  isBefore,
  isFriday,
  isMonday,
  isThursday,
  isTuesday,
  isWednesday,
  isWeekend,
  nextFriday,
  nextMonday,
  nextThursday,
  nextTuesday,
  nextWednesday,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from 'date-fns';
import {
  averageScoreThreshold,
  badScoreThreshold,
  goodScoreThreshold,
} from '@models/classroom/attendance/ClassAttendance';
import {
  ClassProgrammeWithMissedDate,
  MissedAttendanceGroups,
} from '@models/classroom/attendance/MissedAttendanceGroups';
import {
  AttendanceState,
  AttendanceStateCheckResult,
} from '../../../pages/classroom/attendance/components/attendance-list/attendance-list.types';
import {
  ChildAttendance,
  TrackAttendanceModelInput,
} from '@store/attendance/attendance.types';
import { isWorkingDay } from '../../common/date.utils';
import { Weekdays } from '../../practitioner/playgroups-utils';

export const isValidAttendableDate = (
  date: Date,
  programmeAttendanceDays: number[],
  holidays: HolidayDto[]
) => {
  let isValid = isWorkingDay(date, holidays);

  if (!isValid || programmeAttendanceDays.length === 0) return false;

  isValid = isAttendableDay(date, programmeAttendanceDays);

  return isValid;
};

export const isAttendableDay = (
  date: Date,
  programmeAttendanceDays: number[]
) => {
  let dayFound = false;
  for (const scheduleDay of programmeAttendanceDays) {
    if (dayFound) break;

    switch (scheduleDay) {
      case Weekdays.mon:
        dayFound = isMonday(date);
        break;
      case Weekdays.tue:
        dayFound = isTuesday(date);
        break;
      case Weekdays.wed:
        dayFound = isWednesday(date);
        break;
      case Weekdays.thu:
        dayFound = isThursday(date);
        break;
      case Weekdays.fri:
        dayFound = isFriday(date);
        break;
    }
  }
  return dayFound;
};

export const nextAttendableDateAfterStartDate = (
  programmeStartDate: Date,
  meetingDay: number
) => {
  let nextAttendableDate = new Date();

  switch (meetingDay) {
    case Weekdays.mon:
      nextAttendableDate = nextMonday(programmeStartDate);
      break;
    case Weekdays.tue:
      nextAttendableDate = nextTuesday(programmeStartDate);
      break;
    case Weekdays.wed:
      nextAttendableDate = nextWednesday(programmeStartDate);
      break;
    case Weekdays.thu:
      nextAttendableDate = nextThursday(programmeStartDate);
      break;
    case Weekdays.fri:
      nextAttendableDate = nextFriday(programmeStartDate);
      break;
    default:
      nextAttendableDate = programmeStartDate;
  }
  return nextAttendableDate;
};

export const getMissedClassAttendance = (
  classRoomGroups: ClassroomGroupDto[], // kept for API compatibility (unused internally now)
  classProgrammes: ClassProgrammeDto[],
  attendance: AttendanceDto[],
  referenceDate: Date = new Date(),
  classroomGroupLearners?: LearnerDto[]
): ClassProgrammeDto[] => {
  return findMissedProgrammes(
    classProgrammes,
    attendance,
    classroomGroupLearners,
    referenceDate,
    false // do not include missedDate
  );
};

const getLastNDays = (referenceDate: Date, days: number): Date[] => {
  const result: Date[] = [];
  for (let i = 0; i < days; i++) {
    result.push(subDays(referenceDate, i));
  }
  return result;
};

const findMissedProgrammes = <T extends boolean>(
  classProgrammes: ClassProgrammeDto[],
  attendance: AttendanceDto[],
  classroomGroupLearners: LearnerDto[] | undefined,
  referenceDate: Date,
  includeMissedDate: T
): T extends true ? ClassProgrammeWithMissedDate[] : ClassProgrammeDto[] => {
  const result: (ClassProgrammeDto | ClassProgrammeWithMissedDate)[] = [];

  const dateForThe2ndLast = new Date();
  dateForThe2ndLast.setDate(-1);
  const diffDays = differenceInDays(referenceDate, dateForThe2ndLast);
  const lastDays = getLastNDays(referenceDate, diffDays);

  for (const pastDate of lastDays) {
    if (isWeekend(pastDate)) continue;

    const dayOfWeek = getDay(pastDate);
    const normalizedDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

    // Programmes that run on this weekday AND had already started by this pastDate
    const programmesForThisDay = classProgrammes.filter((programme) => {
      const programmeStart = new Date(programme.programmeStartDate);
      return (
        programme.meetingDay === normalizedDayOfWeek &&
        programmeStart.getTime() <= pastDate.getTime()
      );
    });

    for (const programme of programmesForThisDay) {
      const missedDayStart = startOfDay(pastDate);
      const missedDayEnd = endOfDay(pastDate);

      const programmeAttendance = attendance.filter(
        (a) => a.classroomProgrammeId === programme.id
      );

      // Active learners on this exact date
      const activeLearners = (classroomGroupLearners ?? []).filter(
        (learner) => {
          if (learner.classroomGroupId !== programme.classroomGroupId)
            return false;

          const learnerStart = startOfDay(new Date(learner.startedAttendance));
          const learnerStop = learner.stoppedAttendance
            ? endOfDay(new Date(learner.stoppedAttendance))
            : null;

          return (
            missedDayStart.getTime() >= learnerStart.getTime() &&
            (!learnerStop || missedDayEnd.getTime() <= learnerStop.getTime())
          );
        }
      );

      if (activeLearners.length === 0) continue;

      // Check if every learner has attendance recorded for this day
      const allHaveAttendance = activeLearners.every((learner) =>
        programmeAttendance.some((att) => {
          if (att.userId !== learner.userId) return false;
          return (
            startOfDay(new Date(att.attendanceDate!)).getTime() ===
            missedDayStart.getTime()
          );
        })
      );

      if (!allHaveAttendance) {
        const isDuplicate = result.some((item) => {
          if (item.id !== programme.id) return false;
          if (!includeMissedDate) return true;
          return (
            startOfDay(
              (item as ClassProgrammeWithMissedDate).missedDate
            ).getTime() === missedDayStart.getTime()
          );
        });

        if (!isDuplicate) {
          if (includeMissedDate) {
            result.push({
              ...programme,
              missedDate: missedDayStart,
            } as ClassProgrammeWithMissedDate);
          } else {
            result.push(programme);
          }
        }
      }
    }
  }

  // Sort by missed date (oldest first) when returning historic data
  if (includeMissedDate) {
    (result as ClassProgrammeWithMissedDate[]).sort(
      (a, b) => a.missedDate.getTime() - b.missedDate.getTime()
    );
  }

  return result as T extends true
    ? ClassProgrammeWithMissedDate[]
    : ClassProgrammeDto[];
};

export const getHistoricMissedClassAttendance = (
  classRoomGroups: ClassroomGroupDto[],
  classProgrammes: ClassProgrammeDto[],
  attendance: AttendanceDto[],
  referenceDate: Date = new Date(),
  classroomGroupLearners?: LearnerDto[]
): ClassProgrammeWithMissedDate[] => {
  return findMissedProgrammes(
    classProgrammes,
    attendance,
    classroomGroupLearners,
    referenceDate,
    true
  );
};

export const removeDuplicates = (arr: MissedAttendanceGroups[]) => {
  const seen = new Set();

  return arr.filter((obj) => {
    const classroomGroupId = obj.classroomGroup.id;
    if (!seen.has(classroomGroupId)) {
      seen.add(classroomGroupId);
      return true;
    }
    return false;
  });
};

export const isPractitionerAttendanceMissingForLearner = (
  classRoomGroup: ClassroomGroupDto[],
  classProgrammes: ClassProgrammeDto[],
  learner: LearnerDto,
  attendance: AttendanceDto[],
  date: Date
) => {
  const learnerGroups = classRoomGroup.filter(
    (x) => x.id === learner.classroomGroupId
  );

  const learnerProgrammes = classProgrammes.filter((el) => {
    return learnerGroups.some((f) => {
      return f.id === el.classroomGroupId;
    });
  });
  const missedAttendanceClassProgramme = getMissedClassAttendanceForLearner(
    learnerGroups,
    learnerProgrammes,
    attendance,
    date,
    learner
  );

  return missedAttendanceClassProgramme &&
    missedAttendanceClassProgramme.length > 0
    ? true
    : false;
};

export const mapTrackAttendance = (
  programmeOwnerId: string,
  attendees: ChildAttendance[],
  attendanceDate: string,
  classroomProgrammeId: string,
  edited?: boolean
) => {
  return {
    classroomProgrammeId: classroomProgrammeId,
    attendanceDate: attendanceDate,
    attendees,
    programmeOwnerId,
    edited,
  } as TrackAttendanceModelInput;
};

export const getMissedClassAttendanceForLearner = (
  classRoomGroups: ClassroomGroupDto[],
  classProgrammes: ClassProgrammeDto[],
  attendance: AttendanceDto[],
  date: Date,
  learner: LearnerDto
) => {
  const dayOfWeek = getDay(date);
  const currentDayFilter = dayOfWeek === 0 ? 7 : dayOfWeek;
  const returnProgrammes: ClassProgrammeDto[] = [];
  const groupProgrammes = classProgrammes;

  // all the class programs for up until today but does not check the start date
  const classProgrammesUpToCurrentDay = groupProgrammes?.filter((x) => {
    const programStartDate =
      typeof x.programmeStartDate !== 'undefined'
        ? new Date(x.programmeStartDate)
        : new Date();
    const programStartDateDay = getDayOfYear(programStartDate);
    const dateDay = getDayOfYear(date);

    return programStartDateDay === dateDay
      ? (x.meetingDay || -1) === currentDayFilter
      : (x.meetingDay || -1) <= currentDayFilter &&
          isBefore(programStartDateDay, dateDay);
  });

  const learnerAttendance = attendance.filter((x) => {
    return x.userId === learner.userId;
  });

  const meetingDays = getClassroomGroupSchoolDays(
    classProgrammesUpToCurrentDay
  );
  const startOfWeekDate = startOfWeek(new Date().setHours(23, 59, 59, 999), {
    weekStartsOn: 1,
  });

  if (classProgrammesUpToCurrentDay)
    for (const programme of classProgrammesUpToCurrentDay) {
      const missedDayDate = addDays(startOfWeekDate, programme.meetingDay - 1);
      const isValidDay =
        isValidAttendableDate(missedDayDate, meetingDays || [], []) &&
        missedDayDate.getTime() >=
          new Date(learner.startedAttendance).getTime();

      if (
        isValidDay &&
        !learnerAttendance.some(
          (att) => att.classroomProgrammeId === programme.id
        )
      ) {
        returnProgrammes.push(programme);
      }
    }
  return returnProgrammes;
};

export const getMonthName = (monthOfYear: number) => {
  if (monthOfYear < 0 || monthOfYear > 11) return 'Invalid month';
  const currentYear = new Date().getFullYear();
  return format(new Date(currentYear, monthOfYear), 'MMMM');
};

export const getPrevMonth = () => {
  let prevMonth = new Date();
  prevMonth.setMonth(prevMonth.getMonth() - 1);
  return prevMonth;
};

export function getMonthRange(monthName: string) {
  const year = new Date().getFullYear();
  // Parse the month name and get the corresponding month number
  const monthNumber = parse(monthName, 'MMMM', new Date()).getMonth() + 1;
  // Get the start and end date of the month
  const startDate = startOfMonth(new Date(year, monthNumber - 1, 1));

  const endDate = endOfMonth(new Date(year, monthNumber - 1, 1));

  return { startDate, endDate };
}

export const getClassroomGroupSchoolDays = (
  classProgrammes: ClassProgrammeDto[]
) => {
  const allMeetingDays = classProgrammes?.map((prog) => prog.meetingDay);
  return allMeetingDays;
};

export const getAllMissedAttendanceGroupsByClassroomGroupId = (
  missedAttendanceGroups: MissedAttendanceGroups[],
  id: string
) => {
  const missedDays: Date[] = [];
  missedAttendanceGroups.forEach((obj) => {
    if (obj.classroomGroup.id === id) {
      const missedDay = new Date(obj.missedDay);
      if (!isNaN(missedDay.getTime())) {
        missedDays.push(missedDay);
      }
    }
  });

  return missedDays;
};

export const getMissedAttendanceSummaryGroups = (
  classroomGroups: ClassroomGroupDto[],
  classProgrammes: ClassProgrammeDto[],
  attendance: AttendanceDto[],
  holidays: HolidayDto[],
  currentDate: Date,
  classroomGroupLearners: LearnerDto[]
) => {
  const dayOfWeek = getDay(currentDate);
  const currentDayFilter = dayOfWeek === 0 ? 7 : dayOfWeek;

  const classProgrammesUpToCurrentDay = classProgrammes?.filter((x) => {
    const programStartDate =
      typeof x.programmeStartDate !== 'undefined'
        ? new Date(x.programmeStartDate)
        : new Date();

    return programStartDate.getUTCDate() === currentDate.getUTCDate()
      ? (x.meetingDay || -1) === currentDayFilter
      : (x.meetingDay || -1) <= currentDayFilter &&
          programStartDate.getTime() < currentDate.getTime();
  });

  const meetingDays = getClassroomGroupSchoolDays(
    classProgrammesUpToCurrentDay
  );

  if (classroomGroups?.length > 0) {
    const attendanceToDoList: MissedAttendanceGroups[] = [];

    if (classroomGroups?.length > 0) {
      const missedAttendance = getMissedClassAttendance(
        classroomGroups,
        classProgrammes,
        attendance,
        currentDate,
        classroomGroupLearners
      );

      const startOfWeekDate = startOfWeek(currentDate, { weekStartsOn: 1 });

      for (const classroomGroup of classroomGroups) {
        const currentGroupMissedAttendance = missedAttendance.filter(
          (x) => x.classroomGroupId === classroomGroup.id
        );
        for (const missedAttendanceClassProgramme of currentGroupMissedAttendance) {
          const missedDayDate = addDays(
            startOfWeekDate,
            missedAttendanceClassProgramme.meetingDay - 1
          );

          const isValidDay = isValidAttendableDate(
            missedDayDate,
            meetingDays || [],
            holidays
          );

          if (isValidDay) {
            attendanceToDoList.push({
              classroomGroup: classroomGroup,
              missedDay: missedDayDate,
              classProgramme: missedAttendanceClassProgramme,
            });
          }
        }
      }

      return attendanceToDoList.sort((a, b) =>
        a.missedDay > b.missedDay ? 1 : -1
      );
    }
  }

  return [];
};

export const getMissedAttendanceSummaryGroupsForEditRegisters = (
  classroomGroups: ClassroomGroupDto[],
  classProgrammes: ClassProgrammeDto[],
  attendance: AttendanceDto[],
  holidays: HolidayDto[],
  currentDate: Date,
  classroomGroupLearners: LearnerDto[]
): MissedAttendanceGroups[] => {
  if (!classroomGroups?.length) {
    return [];
  }

  const missedHistoricProgrammes = getHistoricMissedClassAttendance(
    classroomGroups,
    classProgrammes,
    attendance,
    currentDate,
    classroomGroupLearners
  );

  const attendanceToDoList: MissedAttendanceGroups[] = [];

  const meetingDays = getClassroomGroupSchoolDays(classProgrammes);

  for (const classroomGroup of classroomGroups) {
    // Get all missed programmes for this specific classroom group
    const groupMissedProgrammes = missedHistoricProgrammes.filter(
      (p) => p.classroomGroupId === classroomGroup.id
    );

    for (const missedProg of groupMissedProgrammes) {
      const missedDayDate = missedProg.missedDate;

      const isValidDay = isValidAttendableDate(
        missedDayDate,
        meetingDays || [],
        holidays
      );

      if (isValidDay) {
        attendanceToDoList.push({
          classroomGroup: classroomGroup,
          missedDay: missedDayDate,
          classProgramme: missedProg,
        });
      }
    }
  }

  // Sort by missed day (oldest first)
  return attendanceToDoList.sort(
    (a, b) => a.missedDay.getTime() - b.missedDay.getTime()
  );
};

export const getAttendanceStatusCheck = (
  attendanceGroups: AttendanceState[],
  currentValidStatus: boolean
): AttendanceStateCheckResult => {
  let presentCount = 0,
    absentCount = 0;

  let isValid = currentValidStatus;

  for (const attendanceList of attendanceGroups) {
    if (attendanceList.isRequired) {
      isValid = attendanceList.list.every(
        (x) =>
          x.status === AttendanceStatus.Present ||
          x.status === AttendanceStatus.Absent
      );
    }
    //TD: test t-eq
    presentCount += attendanceList.list.filter(
      (x) => x.status === AttendanceStatus.Present
    ).length;

    absentCount += attendanceList.list.filter(
      (x) => x.status === AttendanceStatus.Absent
    ).length;
  }

  return {
    isValid,
    presentCount,
    absentCount,
  };
};

export const classroomGroupHasAttendanceOnDate = (
  classProgrammes: ClassProgrammeDto[],
  date: Date,
  selectedclassroomGroupId?: string
): ClassProgrammeDto | undefined => {
  if (selectedclassroomGroupId === '') {
    return classProgrammes
      ? classProgrammes.find((x) => x.meetingDay === getDay(date))
      : undefined;
  } else {
    return classProgrammes
      ? classProgrammes.find(
          (x) =>
            x.meetingDay === getDay(date) &&
            x.classroomGroupId === selectedclassroomGroupId
        )
      : undefined;
  }
};

export const classroomGroupHasAttendanceDate = (
  classProgrammes: ClassProgrammeDto[],
  date: Date,
  previousClassroomID?: string
): ClassProgrammeDto | undefined => {
  return classProgrammes
    ? classProgrammes.length > 5
      ? classProgrammes.find(
          (x) =>
            x.classroomGroupId !== previousClassroomID &&
            x.meetingDay === getDay(date)
        )
      : classProgrammes.find((x) => x.meetingDay === getDay(date))
    : undefined;
};

export const getPlaygroups = (
  classProgrammes: ClassProgrammeDto[],
  date: Date,
  selectedClassroomGroupIds?: string[]
) => {
  if (!selectedClassroomGroupIds || selectedClassroomGroupIds.length === 0) {
    return classProgrammes?.filter((x) => x.meetingDay === getDay(date));
  } else {
    return classProgrammes?.filter(
      (x) =>
        x.meetingDay === getDay(date) &&
        selectedClassroomGroupIds.includes(x.classroomGroupId)
    );
  }
};

export const getPlaygroup = (
  classProgrammes: ClassProgrammeDto[],
  date: Date,
  selectedclassroomGroupId?: string
) => {
  if (selectedclassroomGroupId === '') {
    return classProgrammes?.find((x) => x.meetingDay === getDay(date));
  } else {
    return classProgrammes?.find(
      (x) =>
        x.meetingDay === getDay(date) &&
        x.classroomGroupId === selectedclassroomGroupId
    );
  }
};

export const getDistinctMeetingDays = (attendance: AttendanceDto[]) => {
  return attendance.reduce((prev, curr) => {
    const attendanceDate = new Date(curr.attendanceDate ?? '');
    const dayOfCurrentItem = getDay(attendanceDate || 0);
    if (prev.indexOf(dayOfCurrentItem) === -1) {
      return [...prev, dayOfCurrentItem];
    }
    return prev;
  }, [] as number[]);
};

export const getShape = (score: number): SubTitleShape => {
  if (score >= goodScoreThreshold) {
    return 'circle';
  }

  if (score > badScoreThreshold) {
    return `triangle`;
  }

  return 'square';
};

export const getShapeClass = (type: SubTitleShape, subTitleColor: Colours) => {
  switch (type) {
    case 'square':
      return `h-2.5 w-2.5 bg-${subTitleColor}`;
    case 'triangle':
      return `h-0 w-0 border-opacity-0 border-t-0 border-l-5 border-l-tranparent border-r-5 border-r-tranparent border-b-10 border-b-${subTitleColor} shadow-none`;
    case 'circle':
      return `h-2.5 w-2.5 rounded-full bg-${subTitleColor}`;
    default:
      return `h-2.5 w-2.5 rounded-full bg-${subTitleColor}`;
  }
};

export const getColor = (score: number): Colours => {
  if (score >= goodScoreThreshold) {
    return 'successDark';
  }

  if (score >= averageScoreThreshold) {
    return 'alertDark';
  }

  return 'errorDark';
};
